import React, { useRef } from 'react';
import { makeStyles } from '@fluentui/react-components';
import { SectionQuestion as SectionQuestionType } from '../../../../types/forms';

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    width: '100%',
    position: 'relative',
    padding: '20px',
    marginBottom: '16px',
  },
  sectionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
  },
  titleInputWrapper: {
    backgroundColor: 'white',
    borderRadius: '4px',
    position: 'relative',
    minHeight: '48px',
    display: 'flex',
    alignItems: 'center',
    padding: '10px',
  },
  titleInput: {
    flex: '1',
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '24px',
    fontWeight: 600,
    lineHeight: '32px',
    color: '#616161',
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    padding: '0',
    '::placeholder': {
      color: '#a19f9d',
    },
  },
  titleInputBorder: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    height: '2px',
    backgroundColor: '#03787c',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
  },
  descriptionInputWrapper: {
    backgroundColor: 'white',
    borderRadius: '4px',
    padding: '6px 10px',
    minHeight: '32px',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  },
  descriptionInput: {
    flex: '1',
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#616161',
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    padding: '0',
    minHeight: '20px',
    resize: 'none',
    '::placeholder': {
      color: '#a19f9d',
    },
  },
  descriptionInputBorder: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    height: '2px',
    backgroundColor: '#03787c',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
  },
});

interface SectionQuestionProps {
  question: SectionQuestionType;
  questionNumber: number;
  onChange: (question: SectionQuestionType) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

export const SectionQuestion: React.FC<SectionQuestionProps> = ({
  question,
  onChange,
}) => {
  const styles = useStyles();
  const [isTitleFocused, setIsTitleFocused] = React.useState(false);
  const [isDescriptionFocused, setIsDescriptionFocused] = React.useState(false);
  const titleRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...question,
      title: e.target.value,
    });
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange({
      ...question,
      description: e.target.value,
    });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <div className={styles.titleInputWrapper}>
            <input
              ref={titleRef}
              type="text"
              className={styles.titleInput}
              value={question.title}
              onChange={handleTitleChange}
              onFocus={() => setIsTitleFocused(true)}
              onBlur={() => setIsTitleFocused(false)}
              placeholder="Section"
            />
            {isTitleFocused && <div className={styles.titleInputBorder} />}
          </div>
          <div className={styles.descriptionInputWrapper}>
            <textarea
              ref={descriptionRef}
              className={styles.descriptionInput}
              value={question.description || ''}
              onChange={handleDescriptionChange}
              onFocus={() => setIsDescriptionFocused(true)}
              onBlur={() => setIsDescriptionFocused(false)}
              placeholder="Section description (optional)"
              rows={1}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = `${target.scrollHeight}px`;
              }}
            />
            {isDescriptionFocused && <div className={styles.descriptionInputBorder} />}
          </div>
        </div>
      </div>
    </div>
  );
};
/* force reload */
